import { registerOneReportMedia } from '@brands/services/oneReport/registerOneReportMedia';
import { confirmOneReportMedia } from '@brands/services/oneReport/confirmOneReportMedia';
import { IGMRegisteredFile } from '@brands/services/communication/types/IGMRegisteredFile';
import { UploadedFile } from '@brands/store/slices/patientFormSlice';
import { displayErrorDetails } from '@brands/Utils/displayError';
import { setPreviewCache } from '@brands/Utils/mediaPreviewCache';
import { getUploadFileMimeType } from './caseAttachmentFiles';

const mimeOf = (file: File): string => getUploadFileMimeType(file);

// Uploads a single prepared File to its presigned URL and confirms the Media
// record. Shared by the single- and batch-file entry points below.
async function putAndConfirm(
  file: File,
  registeredFile: IGMRegisteredFile | undefined,
  userRole?: string,
): Promise<UploadedFile> {
  if (!registeredFile) throw new Error('registerOneReportMedia returned fewer files than requested');
  const mimetype = mimeOf(file);
  const { upload_id: mediaId, upload_url: uploadUrl } = registeredFile;

  const putResponse = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'content-type': mimetype },
  });
  if (!putResponse.ok) {
    throw new Error(`S3 PUT failed: ${putResponse.status} ${putResponse.statusText}`);
  }

  await confirmOneReportMedia(mediaId, userRole);
  setPreviewCache(mediaId, file);

  return { name: file.name, media_id: mediaId, mimetype };
}

// Single-file upload. Throws on any failure so the caller can toast / retry.
export async function uploadIntakeImage(file: File, userRole?: string): Promise<UploadedFile> {
  const registered = await registerOneReportMedia(
    { files: [{ filename: file.name, mimetype: mimeOf(file) }] },
    userRole,
  );
  return putAndConfirm(file, registered.files[0], userRole);
}

// Batch upload. One register call for all files, then parallel PUT + confirm
// per file. Register failure throws (nothing uploaded). Per-file PUT/confirm
// failures are toasted and dropped from the result; partial success is valid.
export async function uploadIntakeImages(files: File[], userRole?: string): Promise<UploadedFile[]> {
  if (files.length === 0) return [];

  const registered = await registerOneReportMedia(
    { files: files.map((f) => ({ filename: f.name, mimetype: mimeOf(f) })) },
    userRole,
  );

  const results = await Promise.all(
    files.map(async (file, i) => {
      try {
        return await putAndConfirm(file, registered.files[i], userRole);
      } catch (error: unknown) {
        displayErrorDetails(error);
        return null;
      }
    }),
  );

  return results.filter((r): r is UploadedFile => r !== null);
}
